import React, { useState, useCallback } from 'react'
import {
  IconBold,
  IconBookmark,
  IconCheck,
  IconH1,
  IconH2,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconPhoto,
  IconSeparatorHorizontal,
} from '@tabler/icons-react'
import { Editor, Transforms, Range } from 'slate'

import { toast } from '@/components/toasts/toast.helpers'

import ToolbarButton from './ToolbarButton'
import UrlInput from './UrlInput'

const Toolbar = ({ editor, CustomEditor, inlineVerbatim }) => {
  const [showUrlInput, setShowUrlInput] = useState(false)
  const [urlInputValue, setUrlInputValue] = useState('')
  const [urlInputType, setUrlInputType] = useState('')

  // URL validation regex
  const urlRegex =
    /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/

  const promptForUrl = useCallback(
    (e, urlType) => {
      e.preventDefault()
      const { selection } = editor

      // Check if we have any active links or buttons
      const [link] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && n.type === 'link',
      }) || [null]

      const [button] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && n.type === 'button',
      }) || [null]

      // If link/button is selected, remove it
      if (link) {
        Transforms.unwrapNodes(editor, {
          match: (n) => n.type === 'link',
          split: true,
        })
        return
      }
      if (button) {
        Transforms.unwrapNodes(editor, {
          match: (n) => n.type === 'button',
          split: true,
        })
        return
      }

      // Show URL input if we have a selection, or it's an image
      if (
        (selection &&
          !Range.isCollapsed(selection) &&
          (urlType === 'link' || urlType === 'button')) ||
        urlType === 'image'
      ) {
        setShowUrlInput(true)
        setUrlInputType(urlType)
      }
    },
    [editor]
  )

  const toolbarButtons = [
    {
      slateObject: 'mark',
      slateObjectType: 'bold',
      icon: <IconBold />,
      testId: 'bold-icon',
      handleMouseDown: (e) => {
        e.preventDefault()
        CustomEditor.toggleBoldMark(editor)
      },
    },
    {
      slateObject: 'mark',
      slateObjectType: 'italic',
      icon: <IconItalic />,
      testId: 'italic-icon',
      handleMouseDown: (e) => {
        e.preventDefault()
        CustomEditor.toggleItalicMark(editor)
      },
    },
    {
      slateObject: 'node',
      slateObjectType: 'h1',
      icon: <IconH1 />,
      testId: 'heading-icon',
      handleMouseDown: (e) => {
        e.preventDefault()
        CustomEditor.toggleBlock(editor, 'h1')
      },
    },
    {
      slateObject: 'node',
      slateObjectType: 'h2',
      icon: <IconH2 />,
      testId: 'heading-2-icon',
      handleMouseDown: (e) => {
        e.preventDefault()
        CustomEditor.toggleBlock(editor, 'h2')
      },
    },
    {
      slateObject: 'node',
      slateObjectType: 'numbered-list',
      icon: <IconListNumbers />,
      testId: 'numbered-list',
      handleMouseDown: (e) => {
        e.preventDefault()
        CustomEditor.toggleBlock(editor, 'numbered-list')
      },
    },
    {
      slateObject: 'node',
      slateObjectType: 'bulleted-list',
      icon: <IconList />,
      testId: 'bulleted-list',
      handleMouseDown: (e) => {
        e.preventDefault()
        CustomEditor.toggleBlock(editor, 'bulleted-list')
      },
    },
    {
      slateObject: 'node',
      slateObjectType: 'image',
      icon: <IconPhoto />,
      testId: 'image-icon',
      handleMouseDown: (e) => promptForUrl(e, 'image'),
    },
    {
      slateObject: 'inline',
      slateObjectType: 'link',
      icon: <IconLink />,
      testId: 'link-icon',
      handleMouseDown: (e) => promptForUrl(e, 'link'),
    },
    {
      slateObject: 'inline',
      slateObjectType: 'button',
      icon: <IconBookmark />,
      testId: 'button-icon',
      handleMouseDown: (e) => promptForUrl(e, 'button'),
    },
    {
      slateObject: 'node',
      slateObjectType: 'horizontal-rule',
      icon: <IconSeparatorHorizontal />,
      testId: 'hr-icon',
      handleMouseDown: (e) => {
        e.preventDefault()
        CustomEditor.insertHorizontalRule(editor)
      },
    },
  ]

  // Add inline verbatim button if enabled
  if (inlineVerbatim) {
    toolbarButtons.push({
      slateObject: 'node',
      slateObjectType: 'inline-verbatim-container',
      icon: <IconCheck />,
      testId: 'inline-verbatim-icon',
      handleMouseDown: (e) => {
        e.preventDefault()
        CustomEditor.toggleInlineVerbatim(editor)
      },
    })
  }

  const resetUrlInput = useCallback(() => {
    setShowUrlInput(false)
    setUrlInputType('')
    setUrlInputValue('')
  }, [])

  const handleUrlSubmit = useCallback(
    (e) => {
      e.preventDefault()

      if (urlInputType === 'image') {
        CustomEditor.insertImage(editor, urlInputValue)
      } else {
        if (!urlInputValue.match(urlRegex)) {
          toast.error('Please enter a valid URL')
          setUrlInputValue('')
          return
        }
        if (urlInputType === 'link') {
          CustomEditor.wrapLink(editor, urlInputValue)
        } else if (urlInputType === 'button') {
          Transforms.wrapNodes(
            editor,
            { type: 'button', url: urlInputValue, children: [] },
            { split: true }
          )
        }
      }

      resetUrlInput()
    },
    [editor, urlInputType, urlInputValue, resetUrlInput]
  )

  const updateUrlValue = useCallback((e) => {
    e.preventDefault()
    setUrlInputValue(e.target.value)
  }, [])

  const isMarkActive = useCallback(
    (type) => {
      if (type === 'bold') return CustomEditor.isBoldMarkActive(editor)
      if (type === 'italic') return CustomEditor.isItalicMarkActive(editor)
      return false
    },
    [editor]
  )

  const isBlockActive = useCallback(
    (type) => {
      return CustomEditor.isBlockActive(editor, type)
    },
    [editor]
  )

  const isInlineActive = useCallback(
    (type) => {
      const [match] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && n.type === type,
      }) || [null]
      return !!match
    },
    [editor]
  )

  const getIsActive = useCallback(
    (slateObject, type) => {
      switch (slateObject) {
        case 'mark':
          return isMarkActive(type)
        case 'node':
          return isBlockActive(type)
        case 'inline':
          return isInlineActive(type)
        default:
          return false
      }
    },
    [isMarkActive, isBlockActive, isInlineActive]
  )

  return (
    <>
      <div className="text-editor-toolbar">
        {toolbarButtons.map((button) => (
          <ToolbarButton
            key={button.slateObjectType}
            isActive={getIsActive(button.slateObject, button.slateObjectType)}
            handleMouseDown={button.handleMouseDown}
            icon={button.icon}
            slateObjectType={button.slateObjectType}
            dataTestId={button.testId}
          />
        ))}
      </div>
      {showUrlInput && (
        <UrlInput
          urlInputValue={urlInputValue}
          updateUrlValue={updateUrlValue}
          urlInputType={urlInputType}
          resetUrlInput={resetUrlInput}
          handleSubmit={handleUrlSubmit}
        />
      )}
    </>
  )
}

export default Toolbar
